import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import PageTemplate from './PageTemplate';
import './ModulePage.css';

function normalizeStudentsResponse(data) {
  // Handle different response formats from /api/students/unpaid/
  // Response structure: { message: "...", students: [...] } or { message: "...", students: { results: [...], count: ... } }
  let list = [];
  
  if (Array.isArray(data)) {
    list = data;
  } else if (data?.students) {
    // Handle wrapped response with 'students' key
    if (Array.isArray(data.students)) {
      list = data.students;
    } else if (data.students.results && Array.isArray(data.students.results)) {
      // Paginated response: { students: { results: [...], count: ... } }
      list = data.students.results;
    }
  } else if (data?.results && Array.isArray(data.results)) {
    // Direct paginated response: { results: [...], count: ... }
    list = data.results;
  }

  if (!Array.isArray(list)) return [];

  return list.map((s, idx) => {
    const id = s?.id ?? idx;
    // UnpaidStudentSerializer returns 'name' instead of 'full_name'
    const fullName = s?.name ?? s?.full_name ?? '';
    // UnpaidStudentSerializer returns 'email' directly
    const email = s?.email ?? s?.email_id ?? '';
    // UnpaidStudentSerializer returns 'phone' instead of 'phone_number'
    const phone = s?.phone ?? s?.phone_number ?? '';
    // UnpaidStudentSerializer returns 'course_name' instead of 'course'
    const course = s?.course_name ?? s?.course ?? '';
    const purchaseDeadline = s?.purchase_deadline ?? null;

    return {
      id,
      fullName: String(fullName),
      email: String(email),
      phone: String(phone),
      course: String(course),
      hasPurchased: false, // All students from this endpoint are unpaid
      purchaseDeadline: purchaseDeadline ? new Date(purchaseDeadline).toISOString() : null,
    };
  });
}

function formatPhoneNumber(phone) {
  const raw = String(phone || '').trim();
  if (!raw) return '—';

  const digits = raw.replace(/\D/g, '');
  if (!digits) return raw;

  if (digits.length === 10) {
    return `${digits.slice(0, 5)} ${digits.slice(5)}`;
  }

  if (digits.length === 12 && digits.startsWith('91')) {
    const local = digits.slice(2);
    return `+91 ${local.slice(0, 5)} ${local.slice(5)}`;
  }

  if (digits.length > 10) {
    const last10 = digits.slice(-10);
    const countryCode = digits.slice(0, -10);
    return `+${countryCode} ${last10.slice(0, 5)} ${last10.slice(5)}`;
  }

  return raw;
}

function UnpaidStudents() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadStudents() {
      setLoading(true);
      setError('');
      try {
        // Use the dedicated unpaid students endpoint
        const res = await apiClient.get('/api/students/unpaid/');
        
        if (cancelled) return;

        const studentsList = normalizeStudentsResponse(res.data);
        setStudents(studentsList);
      } catch (e) {
        if (cancelled) return;
        console.error('Error loading unpaid students:', e);
        console.error('Error response:', e?.response);
        console.error('Error data:', e?.response?.data);
        console.error('Error status:', e?.response?.status);
        
        if (e?.response?.status === 401) {
          navigate('/login', { replace: true });
          return;
        }
        
        let errorMsg =
          e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.message ||
          'Unable to load unpaid students. Please try again.';
        
        // Handle 500 errors with more detail
        if (e?.response?.status === 500) {
          const errorData = e?.response?.data;
          if (errorData) {
            if (typeof errorData === 'string') {
              errorMsg = `Server error: ${errorData}`;
            } else if (errorData.detail) {
              errorMsg = `Server error: ${errorData.detail}`;
            } else if (errorData.message) {
              errorMsg = `Server error: ${errorData.message}`;
            } else {
              errorMsg = 'Server error: The server encountered an error while loading unpaid students. Please check the server logs.';
            }
          } else {
            errorMsg = 'Server error: The server encountered an error while loading unpaid students. Please check the server logs.';
          }
        }
        // Add helpful message for network errors
        else if (!e?.response) {
          const apiUrl = apiClient.defaults.baseURL || 'https://edupravahaa-tech-admin.com';
          if (e?.message?.includes('Network Error') || e?.message?.includes('ERR_NETWORK')) {
            errorMsg = `Network error: Unable to connect to ${apiUrl}. Please check if the API server is running.`;
          }
        }
        
        setError(errorMsg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadStudents();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // Apply search filter on unpaid students (all students from API are already unpaid)
  const filteredStudents = useMemo(() => {
    const term = search.toLowerCase().trim();
    return students.filter((student) => {
      if (!term) return true;
      return (
        student.fullName.toLowerCase().includes(term) ||
        student.email.toLowerCase().includes(term) ||
        student.phone.toLowerCase().includes(term)
      );
    });
  }, [students, search]);

  // Calculate statistics
  const stats = useMemo(() => {
    return {
      total: students.length,
      withDeadline: students.filter((s) => s.purchaseDeadline).length,
      overdue: students.filter((s) => {
        if (!s.purchaseDeadline) return false;
        return new Date(s.purchaseDeadline) < new Date();
      }).length,
    };
  }, [students]);

  return (
    <PageTemplate
      title="Unpaid Students Dashboard"
      subtitle="View and manage students with incomplete subscriptions or payments"
    >
      <div className="students-page">
        {error ? (
          <div className="form-error" style={{ marginBottom: '16px' }}>
            {error}
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                marginLeft: '12px',
                padding: '4px 12px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        ) : null}

        {/* Statistics Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px', 
          marginBottom: '24px' 
        }}>
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '8px' }}>
              Total Unpaid Students
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937' }}>
              {stats.total}
            </div>
          </div>
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '8px' }}>
              With Deadline
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#2563eb' }}>
              {stats.withDeadline}
            </div>
          </div>
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '8px' }}>
              Overdue Payments
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#dc2626' }}>
              {stats.overdue}
            </div>
          </div>
        </div>

        <div className="batch-filters">
          <input
            type="search"
            placeholder="Search unpaid students by name, email, or phone..."
            className="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/students')}
          >
            View All Students
          </button>
        </div>

        <div
          className="teachers-table-wrapper unpaid-students-table-wrapper"
          role="region"
          aria-label="Unpaid students table"
        >
          <table className="teachers-table">
            <thead>
              <tr>
                <th>Student ID</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Phone Number</th>
                <th className="table-th-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="teachers-empty teachers-empty--loading">
                    Loading unpaid students…
                  </td>
                </tr>
              ) : filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="5" className="teachers-empty">
                    {search
                      ? 'No unpaid students match your search.'
                      : 'No unpaid students found. All students have completed their payments.'}
                  </td>
                </tr>
              ) : (
                filteredStudents.map((student) => {
                  return (
                    <tr key={student.id}>
                      <td className="table-cell-muted">{student.id}</td>
                      <td className="table-cell-strong">{student.fullName}</td>
                      <td>{student.email}</td>
                      <td className="table-cell-strong">{formatPhoneNumber(student.phone)}</td>
                      <td className="table-td-center">
                        <span className="batch-status-pill batch-status-upcoming">
                          Unpaid
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </PageTemplate>
  );
}

export default UnpaidStudents;

